import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import AiMarkdownLink from 'modules/ai/components/AiMarkdownLink';
import { useAiPanelLinksEnabled } from 'modules/ai/hooks/useAiPanelLinksEnabled';
import {
  stripLegacyPanelLinkPrefix,
  transformAiMarkdownLinkUri,
} from 'modules/ai/utils/resolveAiPanelLink';

import ThinkingDots from './ThinkingDots';
import NaviOrb from './NaviOrb';

type AssistantMessageProps = {
  content?: string;
  thinking?: boolean;
  streaming?: boolean;
};

const AssistantMessage: FC<AssistantMessageProps> = ({
  content,
  thinking = false,
  streaming = false,
}) => {
  const { societyId = '' } = useParams<{ societyId: string }>();
  const panelLinksEnabled = useAiPanelLinksEnabled();

  const displayContent = useMemo(() => {
    if (!panelLinksEnabled || !content) {
      return content ?? '';
    }
    return stripLegacyPanelLinkPrefix(content);
  }, [content, panelLinksEnabled]);

  const markdownComponents = useMemo(() => {
    if (!panelLinksEnabled) {
      return undefined;
    }
    return {
      // eslint-disable-next-line react/no-unstable-nested-components
      a: ({
        href,
        children,
      }: {
        href?: string; // eslint-disable-line
        children?: React.ReactNode; // eslint-disable-line
      }) => (
        <AiMarkdownLink href={href} societyId={societyId}>
          {children}
        </AiMarkdownLink>
      ),
    };
  }, [panelLinksEnabled, societyId]);

  return (
    <div className="navi-assistant-message">
      <div className="navi-assistant-message__orb">
        <NaviOrb size={28} showSparkles={false} />
      </div>
      <div className="navi-assistant-message__bubble">
        {thinking ? (
          <ThinkingDots />
        ) : (
          <div
            className={`navi-assistant-message__markdown${
              streaming ? ' navi-assistant-message__markdown--streaming' : ''
            }`}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
              transformLinkUri={
                panelLinksEnabled ? transformAiMarkdownLinkUri : undefined
              }
            >
              {displayContent}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantMessage;
